import React, { useState } from 'react';
import { MailPlus, Loader2, AlertTriangle, Pencil } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { api } from '../context/AuthContext';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from './ui/alert-dialog';

const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString('de-DE') : '');

export const ZUGANG_FILTER = [
  { key: 'alle', label: 'Alle' },
  { key: 'aktiv', label: 'Aktiv' },
  { key: 'eingeladen', label: 'Eingeladen' },
  { key: 'erinnert', label: 'Erinnert' },
  { key: 'abgelaufen', label: 'Link abgelaufen' },
  { key: 'ohne', label: 'Ohne Zugang' },
  { key: 'zustell', label: 'Zustellung fehlgeschlagen' },
];
export const passtZugangFilter = (u, f) => f === 'alle' ? !!u.zugang_status : f === 'zustell' ? !!u.zustell_problem : u.zugang_status === f;

const STIL = {
  aktiv: 'bg-emerald-50 text-emerald-700',
  eingeladen: 'bg-sky-50 text-sky-700',
  erinnert: 'bg-amber-50 text-amber-800',
  abgelaufen: 'bg-rose-50 text-rose-700',
  ohne: 'bg-gray-100 text-gray-600',
};

const text = (u) => ({
  aktiv: 'Portal aktiv',
  eingeladen: `Eingeladen am ${fmt(u.eingeladen_am)}`,
  erinnert: `Erinnert am ${fmt(u.einladung_erinnert_am)}`,
  abgelaufen: 'Link abgelaufen',
  ohne: 'Kein Portal-Zugang',
}[u.zugang_status]);

export const ZugangBadge = ({ u, toast, onDone }) => {
  if (!u.zugang_status) return null;
  const titel = u.zugang_link_bis && u.zugang_status !== 'aktiv' ? `Link gültig bis ${fmt(u.zugang_link_bis)}` : undefined;
  return (
    <>
    <span className={`inline-flex items-center text-[10px] tracking-wide px-2 py-0.5 rounded-sm mt-1 ${STIL[u.zugang_status]}`} title={titel} data-testid={`zugang-status-${u.user_id}`}>
      {text(u)}
    </span>
    {u.zustell_problem && (
      <span className="inline-flex items-center gap-1 text-[10px] tracking-wide px-2 py-0.5 rounded-sm mt-1 ml-1 bg-rose-600 text-white" title={`${u.zustell_problem.grund} · Mail vom ${fmt(u.zustell_problem.gesendet_am)}`} data-testid={`zustell-problem-${u.user_id}`}>
        <AlertTriangle className="w-3 h-3" /> Zustellung fehlgeschlagen ({u.zustell_problem.grund})
      </span>
    )}
    {u.zustell_problem && toast && <AdresseKorrigierenBtn u={u} toast={toast} onDone={onDone} />}
    </>
  );
};

export const ErneutEinladenBtn = ({ u, toast, onDone }) => {
  const [busy, setBusy] = useState(false);
  if (!u.zugang_status || u.zugang_status === 'aktiv') return null;
  const senden = async () => {
    setBusy(true);
    try {
      const { data } = await api.post('/cases/zugang', { email: u.email, name: u.name });
      toast({ title: 'Einladung gesendet', description: data.mail === 'gesendet' ? `Neuer 7-Tage-Link an ${u.email}` : `Mail: ${data.mail}` });
      onDone();
    } catch (e) {
      toast({ title: 'Fehler', description: e?.response?.data?.detail || 'Einladung fehlgeschlagen', variant: 'destructive' });
    } finally { setBusy(false); }
  };
  return (
    <button type="button" onClick={senden} disabled={busy} title={u.zugang_status === 'ohne' ? 'Zum Portal einladen' : 'Erneut einladen (neuer 7-Tage-Link)'}
      className="w-8 h-8 flex items-center justify-center rounded-sm text-[#b8893d] hover:bg-[#fbf7f0] disabled:opacity-50" data-testid={`user-reinvite-${u.user_id}`}>
      {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <MailPlus className="w-4 h-4" />}
    </button>
  );
};

export const SammelEinladenBtn = ({ anzahl, toast, onDone }) => {
  const [busy, setBusy] = useState(false);
  const senden = async () => {
    setBusy(true);
    try {
      const { data } = await api.post('/cases/zugang/sammel', {}, { timeout: 120000 });
      toast({ title: 'Sammel-Einladung abgeschlossen', description: `${data.gesendet} gesendet · ${data.fehler} Fehler${data.fehler ? ` (${data.fehler_emails.join(', ')})` : ''}`, variant: data.fehler ? 'destructive' : undefined });
      onDone();
    } catch (e) {
      toast({ title: 'Fehler', description: e?.response?.data?.detail || 'Sammel-Einladung fehlgeschlagen', variant: 'destructive' });
    } finally { setBusy(false); }
  };
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <button type="button" disabled={!anzahl || busy} data-testid="zugang-sammel-btn"
          className="ml-auto inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-sm bg-[#1B3A5C] text-white hover:bg-[#15304d] disabled:opacity-40 disabled:cursor-not-allowed">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <MailPlus className="w-3.5 h-3.5" />} Alle abgelaufenen neu einladen ({anzahl})
        </button>
      </AlertDialogTrigger>
      <AlertDialogContent data-testid="zugang-sammel-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>{anzahl} Mandanten neu einladen?</AlertDialogTitle>
          <AlertDialogDescription>Alle Mandanten mit abgelaufenem Link erhalten eine neue Einladung mit einem 7 Tage gültigen Link. Die Erinnerung nach 3 Tagen startet neu.</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="zugang-sammel-cancel">Abbrechen</AlertDialogCancel>
          <AlertDialogAction onClick={senden} data-testid="zugang-sammel-confirm" className="bg-[#1B3A5C] hover:bg-[#15304d]">Jetzt einladen</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export const AdresseKorrigierenBtn = ({ u, toast, onDone }) => {
  const [offen, setOffen] = useState(false);
  const [email, setEmail] = useState('');
  const [fehler, setFehler] = useState('');
  const [busy, setBusy] = useState(false);
  const speichern = async () => {
    setBusy(true); setFehler('');
    try {
      const { data } = await api.post('/cases/zugang/adresse', { user_id: u.user_id, email });
      toast({ title: 'Adresse korrigiert', description: `${data.alt} → ${data.neu} · Einladung ${data.mail === 'gesendet' ? 'gesendet' : data.mail}` });
      setOffen(false); onDone();
    } catch (e) { setFehler(e?.response?.data?.detail || 'Speichern fehlgeschlagen'); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={offen} onOpenChange={(o) => { setOffen(o); if (o) { setEmail(u.email); setFehler(''); } }}>
      <DialogTrigger asChild>
        <button type="button" className="inline-flex items-center gap-1 text-[10px] tracking-wide px-2 py-0.5 rounded-sm mt-1 ml-1 border border-rose-300 text-rose-700 hover:bg-rose-50" data-testid={`adresse-korrigieren-${u.user_id}`}>
          <Pencil className="w-3 h-3" /> Adresse korrigieren
        </button>
      </DialogTrigger>
      <DialogContent className="max-w-md" data-testid="adresse-dialog">
        <DialogHeader>
          <DialogTitle>E-Mail-Adresse korrigieren</DialogTitle>
          <DialogDescription>Die neue Adresse wird überall beim Mandanten übernommen (Konto, Register, Akten, Dokumente, Termine, Zahlungen). Alte Einladungslinks werden ungültig, und es geht sofort eine neue Einladung raus.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <div><p className="text-[10px] tracking-wider text-[#7a8a99] uppercase">Bisherige Adresse</p><p className="font-mono text-rose-700 line-through" data-testid="adresse-alt">{u.email}</p></div>
          <label className="block"><span className="text-[10px] tracking-wider text-[#7a8a99] uppercase">Neue Adresse</span>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && speichern()} autoFocus
              className="mt-1 w-full border border-gray-200 px-3 py-2 font-mono text-sm focus:border-[#b8893d] focus:outline-none" data-testid="adresse-neu-input" />
          </label>
          {fehler && <p className="text-xs text-rose-700 bg-rose-50 border-l-2 border-rose-400 px-3 py-2" data-testid="adresse-fehler">{fehler}</p>}
        </div>
        <DialogFooter>
          <button type="button" onClick={() => setOffen(false)} className="text-xs px-4 py-2 border border-gray-200 rounded-sm" data-testid="adresse-abbrechen">Abbrechen</button>
          <button type="button" onClick={speichern} disabled={busy || !email.trim() || email.trim().toLowerCase() === u.email} className="inline-flex items-center gap-1.5 text-xs px-4 py-2 rounded-sm bg-[#1B3A5C] text-white hover:bg-[#15304d] disabled:opacity-50" data-testid="adresse-speichern">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <MailPlus className="w-3.5 h-3.5" />} Speichern &amp; neu einladen
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
